import { analyticsApiClient } from '../api/analyticsApiClient';
import type {
  QueryConfig,
  WidgetDataRequest,
  WidgetDataResponse,
  WidgetDataResponseV2,
  WidgetDataResponseLegacy,
  WidgetDataError,
  TemplateQueryConfig,
  CustomQueryConfig,
  FilterDefinition,
  FilterOptionsResponse,
  FilterType,
  QueryFilter,
  ColumnSchema,
  ResponseSchema,
  ResponseMeta,
  DataType,
} from '../types/analytics';
import { isV2Response } from '../types/analytics';
import { prepareFiltersForRequest } from '../utils/filterValidation';

/**
 * Options for fetching widget data
 */
export interface FetchWidgetDataOptions {
  /** Enable debug mode to see generated SQL */
  debug?: boolean;
  /** Custom model path (defaults to 'workforce') */
  model?: string;
  /** Owner user ID for shared dashboard context - when viewing another user's dashboard */
  ownerUserId?: number | string;
  /** Use V2 API format (default: true) */
  useV2?: boolean;
}

/**
 * Convert a field key to a human-readable label
 */
function humanizeLabel(key: string): string {
  return key
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Infer data type from a value
 */
function inferDataType(value: unknown): DataType {
  if (value === null || value === undefined) return 'string';
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'number') {
    return Number.isInteger(value) ? 'integer' : 'float';
  }
  if (typeof value === 'string') {
    // Check for datetime patterns
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) return 'datetime';
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return 'date';
  }
  return 'string';
}

/**
 * Convert legacy response to V2 format for uniform handling
 */
function convertLegacyToV2<T>(
  legacy: WidgetDataResponseLegacy<T>
): WidgetDataResponse<T> {
  // Infer columns from first data row
  const firstRow = legacy.data[0] as Record<string, unknown> | undefined;
  const columns: ColumnSchema[] = firstRow
    ? Object.keys(firstRow).map(key => ({
        key,
        label: humanizeLabel(key),
        type: inferDataType(firstRow[key]),
        sortable: true,
      }))
    : [];

  return {
    data: legacy.data,
    schema: { columns },
    meta: {
      query_time_ms: parseInt(legacy.metadata.query_time) || 0,
      row_count: legacy.metadata.row_count,
      total_rows: legacy.metadata.total_rows,
    },
    // Keep legacy metadata for backward compatibility
    metadata: legacy.metadata,
  };
}

/**
 * Convert camelCase keys to snake_case for API request
 */
const camelToSnake = (obj: any): any => {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map(camelToSnake);
  if (typeof obj !== 'object') return obj;

  const converted: Record<string, unknown> = {};
  for (const key in obj) {
    const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
    converted[snakeKey] = camelToSnake(obj[key]);
  }
  return converted;
};

/**
 * Convert snake_case keys to camelCase for API response
 */
const snakeToCamel = (obj: any): any => {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map(snakeToCamel);
  if (typeof obj !== 'object') return obj;

  const converted: Record<string, unknown> = {};
  for (const key in obj) {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    converted[camelKey] = snakeToCamel(obj[key]);
  }
  return converted;
};

/**
 * Analytics Service
 *
 * Provides methods for fetching widget data from the analytics endpoint.
 */
export const analyticsService = {
  /**
   * Fetch widget data from the analytics endpoint
   */
  async fetchWidgetData<T = Record<string, unknown>>(
    queryConfig: QueryConfig,
    options: FetchWidgetDataOptions = {}
  ): Promise<WidgetDataResponse<T>> {
    const { debug = false, model = 'workforce', useV2 = true } = options;

    try {
      const cleanedConfig = { ...queryConfig };

      // Handle filters
      if (cleanedConfig.filters) {
        if (Array.isArray(cleanedConfig.filters)) {
          const firstFilter = cleanedConfig.filters[0] as any;
          if (firstFilter && firstFilter.filter_key && !firstFilter.field) {
            console.error('❌ ERROR: Widget queryConfig contains filter schema instead of filter values!');
            delete cleanedConfig.filters;
          } else {
            const preparedFilters = prepareFiltersForRequest(cleanedConfig.filters);
            if (preparedFilters) {
              cleanedConfig.filters = preparedFilters;
            } else {
              delete cleanedConfig.filters;
            }
          }
        } else if (typeof cleanedConfig.filters === 'object') {
          const filterObj = cleanedConfig.filters as Record<string, any>;
          if (Object.keys(filterObj).length === 0) {
            delete cleanedConfig.filters;
          } else {
            console.warn('⚠️ Legacy object filter format detected. Converting to empty filters.');
            delete cleanedConfig.filters;
          }
        }
      }

      const snakeCaseConfig = camelToSnake(cleanedConfig);

      // Keep ownerUserId as string to preserve precision for large int64 IDs
      const ownerUserIdStr = options.ownerUserId
        ? String(options.ownerUserId)
        : undefined;

      const requestPayload: WidgetDataRequest = {
        query_config: snakeCaseConfig,
        // Include owner_user_id for shared dashboard context (sent as string to preserve precision)
        ...(ownerUserIdStr && { owner_user_id: ownerUserIdStr }),
        // Include model for data source selection
        ...(model && { model }),
      };

      // Build URL with format and debug parameters
      const params = new URLSearchParams();
      if (useV2) {
        params.set('format', 'v2');
      }
      if (debug) {
        params.set('debug', 'true');
      }
      const url = `/widget-data${params.toString() ? `?${params.toString()}` : ''}`;

      if (process.env.NODE_ENV === 'development') {
        console.log('📊 Fetching widget data:', {
          url,
          model,
          debug,
          useV2,
          hasMetricKey: 'metric_key' in snakeCaseConfig,
          hasTable: 'table' in snakeCaseConfig,
          filterCount: Array.isArray(cleanedConfig.filters) ? cleanedConfig.filters.length : 0,
          ownerUserId: options.ownerUserId || null,
        });
      }

      const response = await analyticsApiClient.post(url, requestPayload);
      const camelCaseData = snakeToCamel(response.data);

      // Handle V2 vs legacy response format
      if (isV2Response(camelCaseData)) {
        console.log('✅ Widget data fetched successfully (V2):', {
          rowCount: camelCaseData.meta?.rowCount,
          queryTimeMs: camelCaseData.meta?.queryTimeMs,
          columnCount: camelCaseData.schema?.columns?.length,
        });

        // Add legacy metadata for backward compatibility
        const result: WidgetDataResponse<T> = {
          ...camelCaseData,
          metadata: {
            query_time: `${camelCaseData.meta.queryTimeMs}ms`,
            row_count: camelCaseData.meta.rowCount,
            total_rows: camelCaseData.meta.totalRows,
          },
        };

        return result as WidgetDataResponse<T>;
      } else {
        // Legacy response - convert to V2 format
        console.log('✅ Widget data fetched successfully (legacy):', {
          rowCount: camelCaseData.metadata?.rowCount,
          queryTime: camelCaseData.metadata?.queryTime,
        });

        return convertLegacyToV2(camelCaseData as WidgetDataResponseLegacy<T>) as WidgetDataResponse<T>;
      }
    } catch (error) {
      console.error('❌ Failed to fetch widget data:', error);

      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { status: number; data?: unknown } };
        const status = axiosError.response?.status || 500;
        const data = axiosError.response?.data;

        let errorMessage = 'Failed to fetch widget data';
        if (data && typeof data === 'object' && 'error' in data) {
          errorMessage = (data as { error: string }).error;
        } else if (data && typeof data === 'string') {
          errorMessage = data;
        }

        const widgetError: WidgetDataError = {
          error: errorMessage,
          status,
        };

        throw widgetError;
      }

      throw {
        error: error instanceof Error ? error.message : 'Unknown error occurred',
        status: 500,
      } as WidgetDataError;
    }
  },

  /**
   * Fetch multiple widget data queries in parallel
   */
  async fetchMultipleWidgetData<T = Record<string, unknown>>(
    queries: QueryConfig[],
    options: FetchWidgetDataOptions = {}
  ): Promise<WidgetDataResponse<T>[]> {
    console.log(`📊 Fetching ${queries.length} widget data queries in parallel`);

    try {
      const results = await Promise.all(
        queries.map((query) => this.fetchWidgetData<T>(query, options))
      );

      console.log(`✅ All ${queries.length} widget data queries completed`);
      return results;
    } catch (error) {
      console.error('❌ Failed to fetch multiple widget data queries:', error);
      throw error;
    }
  },

  /**
   * Helper: Fetch widget data from a DashboardWidget's queryConfig
   */
  async fetchWidgetDataFromConfig<T = Record<string, unknown>>(
    queryConfig: Record<string, unknown>,
    options: FetchWidgetDataOptions = {}
  ): Promise<WidgetDataResponse<T>> {
    const config = queryConfig as any;

    let validQueryConfig: QueryConfig;

    if (config.metric_key || config.metricKey) {
      validQueryConfig = {
        metric_key: config.metric_key || config.metricKey,
        ...(config.filters && { filters: config.filters }),
        ...(config.dimensions && { dimensions: config.dimensions }),
        ...(config.time_range && { time_range: config.time_range }),
        ...(config.timeRange && { time_range: config.timeRange }),
        ...(config.order_by && { order_by: config.order_by }),
        ...(config.orderBy && { order_by: config.orderBy }),
        ...(config.limit && { limit: config.limit }),
        ...(config.percentile && { percentile: config.percentile }),
        ...(config.scoping_type && { scoping_type: config.scoping_type }),
      } as TemplateQueryConfig;
    } else if (config.table) {
      validQueryConfig = {
        table: config.table,
        aggregations: config.aggregations || [],
        ...(config.filters && { filters: config.filters }),
        ...(config.dimensions && { dimensions: config.dimensions }),
        ...(config.time_range && { time_range: config.time_range }),
        ...(config.timeRange && { time_range: config.timeRange }),
        ...(config.order_by && { order_by: config.order_by }),
        ...(config.orderBy && { order_by: config.orderBy }),
        ...(config.limit && { limit: config.limit }),
        ...(config.percentile && { percentile: config.percentile }),
        ...(config.scoping_type && { scoping_type: config.scoping_type }),
      } as CustomQueryConfig;
    } else {
      throw new Error('Invalid query config: must have either metric_key or table');
    }

    return this.fetchWidgetData<T>(validQueryConfig, options);
  },
};

// ============================================
// Query Builder Helpers
// ============================================

/**
 * Helper class to build query configurations
 */
export class QueryBuilder {
  private config: Partial<TemplateQueryConfig | CustomQueryConfig> = {};

  withMetricKey(metricKey: string): this {
    (this.config as TemplateQueryConfig).metric_key = metricKey;
    return this;
  }

  withTable(table: string): this {
    (this.config as CustomQueryConfig).table = table;
    return this;
  }

  withAggregations(aggregations: CustomQueryConfig['aggregations']): this {
    (this.config as CustomQueryConfig).aggregations = aggregations;
    return this;
  }

  withFilters(filters: QueryConfig['filters']): this {
    this.config.filters = filters;
    return this;
  }

  addFilter(filter: QueryFilter): this {
    if (!this.config.filters) {
      this.config.filters = [];
    }
    this.config.filters.push(filter);
    return this;
  }

  withDimensions(dimensions: QueryConfig['dimensions']): this {
    this.config.dimensions = dimensions;
    return this;
  }

  withTimeRange(timeRange: QueryConfig['time_range']): this {
    this.config.time_range = timeRange;
    return this;
  }

  withRelativeTimeRange(relative: string): this {
    this.config.time_range = { relative };
    return this;
  }

  withAbsoluteTimeRange(start: string, end: string): this {
    this.config.time_range = { start, end };
    return this;
  }

  withOrderBy(orderBy: QueryConfig['order_by']): this {
    this.config.order_by = orderBy;
    return this;
  }

  withLimit(limit: number): this {
    this.config.limit = limit;
    return this;
  }

  build(): QueryConfig {
    if ('metric_key' in this.config && this.config.metric_key) {
      return this.config as TemplateQueryConfig;
    }
    if ('table' in this.config && this.config.table) {
      return this.config as CustomQueryConfig;
    }
    throw new Error('Query must have either metric_key or table specified');
  }
}

export const createQueryBuilder = (): QueryBuilder => {
  return new QueryBuilder();
};

// ============================================
// Analytics Filter Service
// ============================================

const CACHE_TTL = {
  static: 3600000, // 1 hour
  dynamic: 300000, // 5 minutes
};

interface CacheEntry {
  data: FilterOptionsResponse;
  timestamp: number;
  filterType: FilterType;
}

const filterCache = new Map<string, CacheEntry>();

function isCacheValid(entry: CacheEntry): boolean {
  const now = Date.now();
  const ttl = CACHE_TTL[entry.filterType];
  return now - entry.timestamp < ttl;
}

function getFromCache(filterKey: string): FilterOptionsResponse | null {
  const entry = filterCache.get(filterKey);
  if (entry && isCacheValid(entry)) {
    console.log(`[Filter Service] Cache HIT for filter: ${filterKey}`);
    return entry.data;
  }
  if (entry) {
    console.log(`[Filter Service] Cache EXPIRED for filter: ${filterKey}`);
    filterCache.delete(filterKey);
  }
  return null;
}

function storeInCache(filterKey: string, data: FilterOptionsResponse): void {
  filterCache.set(filterKey, {
    data,
    timestamp: Date.now(),
    filterType: data.filter_type,
  });
  console.log(`[Filter Service] Cached filter: ${filterKey} (type: ${data.filter_type})`);
}

export function clearFilterCache(filterKey?: string): void {
  if (filterKey) {
    filterCache.delete(filterKey);
    console.log(`[Filter Service] Cleared cache for filter: ${filterKey}`);
  } else {
    filterCache.clear();
    console.log('[Filter Service] Cleared all filter cache');
  }
}

export const analyticsFilterService = {
  async listFilters(): Promise<FilterDefinition[]> {
    const response = await analyticsApiClient.get('/filters');
    return response.data;
  },

  async getFilterOptions(
    filterKey: string,
    forceRefresh = false
  ): Promise<FilterOptionsResponse> {
    if (!forceRefresh) {
      const cached = getFromCache(filterKey);
      if (cached) {
        return cached;
      }
    }

    console.log(`[Filter Service] Fetching filter from API: ${filterKey}`);

    const response = await analyticsApiClient.get(`/filters/${filterKey}`);
    const data: FilterOptionsResponse = response.data;

    storeInCache(filterKey, data);

    return data;
  },

  async batchGetFilterOptions(
    filterKeys: string[],
    forceRefresh = false
  ): Promise<Map<string, FilterOptionsResponse>> {
    const results = new Map<string, FilterOptionsResponse>();

    const promises = filterKeys.map(async (key) => {
      try {
        const data = await this.getFilterOptions(key, forceRefresh);
        results.set(key, data);
      } catch (error) {
        console.error(`[Filter Service] Failed to fetch filter ${key}:`, error);
        throw error;
      }
    });

    await Promise.all(promises);

    return results;
  },

  async prefetchTemplateFilters(template: {
    queryConfig: Record<string, unknown>;
  }): Promise<void> {
    const filters = (template.queryConfig as { filters?: Array<{ filter_key: string }> })
      ?.filters || [];

    if (filters.length === 0) {
      return;
    }

    const filterKeys = filters.map((f) => f.filter_key);

    console.log(
      `[Filter Service] Prefetching ${filterKeys.length} filters for template`
    );

    this.batchGetFilterOptions(filterKeys, false).catch((error) => {
      console.error('[Filter Service] Prefetch failed:', error);
    });
  },
};
